/**
 * Pure select→aim→confirm session for Infinite War cards (Phaser-free).
 * Scene owns pointer wiring; this module owns transitions only.
 */
import {
  cardAim,
  cardAimHintKey,
  cardNeedsTarget,
} from "../sim/accessors";
import type { IwAimMode, IwCardId } from "../sim/types";

export type CardCastSession =
  | { phase: "idle" }
  | { phase: "aiming"; card: IwCardId };

export const IDLE_CAST: CardCastSession = { phase: "idle" };

/** Tap a hand card: select (or deselect if same card already aiming). */
export function selectOrToggleCard(
  session: CardCastSession,
  card: IwCardId,
): CardCastSession {
  if (session.phase === "aiming" && session.card === card) return IDLE_CAST;
  return { phase: "aiming", card };
}

export function cancelCast(_session: CardCastSession): CardCastSession {
  return IDLE_CAST;
}

export function selectedCard(session: CardCastSession): IwCardId | null {
  return session.phase === "aiming" ? session.card : null;
}

export function aimHintKeyForSession(session: CardCastSession): string | null {
  if (session.phase !== "aiming") return null;
  return cardAimHintKey(session.card);
}

export type FieldConfirmIntent =
  | { ok: false; reason: "idle" }
  | {
      ok: true;
      card: IwCardId;
      aim: IwAimMode;
      targetX?: number;
      targetY?: number;
    };

/** Board tap while aiming → payload for playCard (confirm modes omit coords). */
export function fieldConfirmIntent(
  session: CardCastSession,
  x: number,
  y: number,
): FieldConfirmIntent {
  if (session.phase !== "aiming") return { ok: false, reason: "idle" };
  const aim = cardAim(session.card);
  if (cardNeedsTarget(aim)) {
    return { ok: true, card: session.card, aim, targetX: x, targetY: y };
  }
  return { ok: true, card: session.card, aim };
}
